import { ReactNode, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Button } from './button'
import { cn } from 'src/utils/style'

interface InlineHelpProps {
  /** The single plain-language sentence shown by default. Keep it to one short
   * sentence and free of raw provider jargon. Omit when the surrounding copy
   * already states the sentence and only the "Why?" affordance is needed. */
  text?: ReactNode
  /** The fuller explanation revealed on demand — never shown until the user asks. */
  detail: ReactNode
  /** Overrides the "Why?" affordance label. */
  whyLabel?: string
  className?: string
  'data-testid'?: string
}

/**
 * Progressive-disclosure help: one plain sentence up front plus a "Why?"
 * affordance that expands to the fuller detail on demand. The detail is not
 * rendered until requested, and the affordance is an inline toggle — it never
 * blocks the primary action or navigation around it.
 */
export const InlineHelp = ({
  text,
  detail,
  whyLabel,
  className,
  'data-testid': testId,
}: InlineHelpProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const detailId = useId()

  return (
    <div className={cn(['text-sm text-text-secondary', className])} data-testid={testId}>
      <p className="flex flex-wrap items-center gap-x-1.5">
        {text != null && <span>{text}</span>}
        <Button
          variant="text"
          size="sm"
          tone="primary"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={detailId}
          icon={
            <ChevronDownIcon
              className={cn(['h-3.5 w-3.5 transition-transform', open && 'rotate-180'])}
              aria-hidden="true"
            />
          }
          data-testid={testId ? `${testId}-why` : undefined}
        >
          {whyLabel ?? t('common.why')}
        </Button>
      </p>
      {open && (
        <p
          id={detailId}
          className="mt-1 text-text-placeholder"
          data-testid={testId ? `${testId}-detail` : undefined}
        >
          {detail}
        </p>
      )}
    </div>
  )
}
